import React, { useState, useMemo } from 'react';
import { X, Plus, Loader2, Send } from 'lucide-react';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';

// =============================================================================
// Bill of Materials — shared parts.
//
// The pure BOM logic (tree explosion, extended-quantity maths, formatting) and
// the three dialogs, kept apart from the page that renders them.
//
// Data model (see backend migrations/2026_bud_bom.sql):
//   - catalog       : md_stock_codes -> { description, type, uom }. `type` is
//                     FG / WIP / RM (from item_type).
//   - linesByParent : bud_bom rows grouped by parent — the SINGLE SOURCE OF
//                     TRUTH for edits. One level deep per parent; the tree is an
//                     explosion built by walking these links. Editing a line
//                     (keyed by parent + component + line_no) therefore flows
//                     into EVERY assembly that consumes it.
//   - mfg           : bud_bom_items -> per-parent production settings
//                     ({ basis: 'unit' | 'batch', batchSize }). Batch control is
//                     a property of the item-as-parent, shared by all its lines.
// =============================================================================

export const SCOPES = [
  { code: 'FG', label: 'Finished Goods' },
  { code: 'WIP', label: 'WIP / Bulk' },
  { code: 'ALL', label: 'All' },
];

export const TYPE_BADGE = {
  FG: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  WIP: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  RM: 'bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400',
  PKG: 'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
};

export const fmtQty = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
export const fmtInt = (n) => Number(n || 0).toLocaleString();

// Is this item batch-controlled (with a usable batch size)?
export const isBatch = (m) => m?.basis === 'batch' && Number(m.batchSize) > 0;

// Node field <- bud_bom column translation, for patching edits back to a line.
export const NODE_TO_LINE = {
  qtyPer: 'qty_per',
  scrap: 'scrap_pct',
  fixedLoss: 'fixed_loss',
  workCenter: 'work_center',
  warehouse: 'issue_warehouse',
  periods: 'valid_periods',
  notes: 'notes',
};

// Compact validity label for the tree: null when all 12 (nothing to flag),
// "Inactive" when none, "P3–P8" when contiguous, else "7/12".
export const periodsSummary = (periods) => {
  if (!periods || periods.length === 12) return null;
  if (periods.length === 0) return 'Inactive';
  const sorted = [...periods].sort((a, b) => a - b);
  const contiguous = sorted.every((p, i) => i === 0 || p === sorted[i - 1] + 1);
  if (contiguous) return sorted.length === 1 ? `P${sorted[0]}` : `P${sorted[0]}–P${sorted[sorted.length - 1]}`;
  return `${sorted.length}/12`;
};

// Explode the single-level BOM links into a tree rooted at `code`. Node ids are
// path-based so the same item appearing twice stays independently expandable.
// Each node carries its (parentCode, lineNo) so edits map back to the source
// bud_bom row. A path-cycle guard (`ancestors`) stops runaway recursion if the
// data ever contains a loop.
export const buildTree = (code, linesByParent, catalog, path = 'r', ancestors = []) =>
  (linesByParent[code] || []).map((line, i) => {
    const item = catalog[line.component_code] || {};
    const id = `${path}.${i}`;
    const nextAncestors = [...ancestors, code];
    const cyclic = nextAncestors.includes(line.component_code);
    return {
      id,
      code: line.component_code,
      parentCode: code,
      lineNo: line.line_no,
      description: item.description || line.component_code,
      type: item.type || 'RM',
      uom: item.uom || '—',
      qtyPer: line.qty_per,
      scrap: line.scrap_pct,
      fixedLoss: line.fixed_loss,
      workCenter: line.work_center || '',
      warehouse: line.issue_warehouse || '',
      periods: line.valid_periods && line.valid_periods.length ? line.valid_periods : [...PERIODS],
      notes: line.notes || '',
      children: cyclic ? [] : buildTree(line.component_code, linesByParent, catalog, id, nextAncestors),
    };
  });

export const findNode = (nodes, id) => {
  for (const n of nodes) {
    if (n.id === id) return n;
    if (n.children?.length) {
      const hit = findNode(n.children, id);
      if (hit) return hit;
    }
  }
  return null;
};

export const collectIds = (nodes, out = []) => {
  nodes.forEach((n) => {
    out.push(n.id);
    if (n.children?.length) collectIds(n.children, out);
  });
  return out;
};

// Flatten the tree into visible rows, carrying depth and cumulative extended
// quantity. Under a batch-controlled parent, qtyPer (and fixedLoss) are per
// batch, so the per-unit contribution divides by the batch size.
export const flattenVisible = (nodes, expanded, mfg, parentMode, depth = 0, parentExt = 1, out = []) => {
  nodes.forEach((n) => {
    const perBatch = isBatch(parentMode);
    const eff = Number(n.qtyPer || 0) * (1 + Number(n.scrap || 0) / 100) + (perBatch ? Number(n.fixedLoss || 0) : 0);
    const ext = parentExt * (perBatch ? eff / Number(parentMode.batchSize) : eff);
    out.push({ ...n, depth, ext, perBatch });
    if (n.children?.length && expanded.has(n.id)) {
      flattenVisible(n.children, expanded, mfg, mfg[n.code], depth + 1, ext, out);
    }
  });
  return out;
};

// All WIP stages (any depth), flattened in tree order — feeds the summary panel.
export const collectWips = (nodes, parentMode, mfg, depth = 0, out = []) => {
  nodes.forEach((n) => {
    if (n.type === 'WIP') out.push({ ...n, depth, perBatch: isBatch(parentMode) });
    if (n.children?.length) collectWips(n.children, mfg[n.code], mfg, depth + 1, out);
  });
  return out;
};

export const inputClass =
  'w-20 text-right text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 ' +
  'text-slate-800 dark:text-slate-100 px-2 py-1 outline-none focus:ring-2 focus:ring-slate-400';

const selectClass =
  'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 ' +
  'text-slate-700 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-slate-400';

export const pillClass = (on) =>
  `px-2.5 py-1.5 text-xs rounded border transition-colors ${
    on
      ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
      : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
  }`;

// Small "BATCH · 1,000 L" chip shown wherever a batch-controlled item appears.
export const BatchChip = ({ mode, uom }) =>
  isBatch(mode) ? (
    <span
      title={`Batch-controlled — component quantities below this item are captured per batch of ${fmtInt(mode.batchSize)} ${uom}`}
      className="shrink-0 inline-block px-1.5 py-0.5 text-[10px] font-bold rounded bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-400"
    >
      BATCH · {fmtInt(mode.batchSize)} {uom}
    </span>
  ) : null;

// ---- Line detail modal --------------------------------------------------------
const DetailSection = ({ title, children }) => (
  <div className="mb-5">
    <h3 className="m-0 mb-2 text-xs font-bold tracking-wider text-slate-500 dark:text-slate-400 uppercase">{title}</h3>
    {children}
  </div>
);

export const LineDetailModal = ({ node, mfg, warehouses, isManufactured, fyStartMonth, onPatch, onPatchMfg, onClose }) => {
  if (!node) return null;

  const togglePeriod = (p) => {
    const next = node.periods.includes(p)
      ? node.periods.filter((x) => x !== p)
      : [...node.periods, p].sort((a, b) => a - b);
    onPatch({ periods: next });
  };

  const validity = periodsSummary(node.periods);
  const manufactured = isManufactured(node.code);
  const ownMode = mfg[node.code];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">

        {/* Header */}
        <div className="sticky top-0 z-10 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold rounded ${TYPE_BADGE[node.type] || ''}`}>{node.type}</span>
              <span className="font-mono text-sm font-bold text-slate-800 dark:text-slate-100">{node.code}</span>
              <BatchChip mode={ownMode} uom={node.uom} />
            </div>
            <p className="m-0 mt-0.5 text-sm text-slate-500 dark:text-slate-400">{node.description}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          {/* Production settings — only for items that are themselves made */}
          {manufactured && (
            <DetailSection title="Production (as parent item)">
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => onPatchMfg(node.code, { basis: 'unit' })}
                  className={pillClass(ownMode?.basis !== 'batch')}
                >
                  Components per unit
                </button>
                <button
                  type="button"
                  onClick={() => onPatchMfg(node.code, { basis: 'batch', batchSize: ownMode?.batchSize || 1000 })}
                  className={pillClass(ownMode?.basis === 'batch')}
                >
                  Components per batch
                </button>
                {ownMode?.basis === 'batch' && (
                  <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    Batch size
                    <input
                      type="number"
                      step="1"
                      min="0"
                      className={inputClass}
                      value={ownMode.batchSize}
                      onChange={(e) => onPatchMfg(node.code, { batchSize: Number(e.target.value) || 0 })}
                    />
                    {node.uom}
                  </label>
                )}
              </div>
              <p className="m-0 mt-1.5 text-xs text-slate-400">
                Applies wherever {node.code} is made: when batch-controlled, ALL component quantities
                below it are captured per batch (the tank view) — no per-line mixing.
              </p>
            </DetailSection>
          )}

          {/* Validity by financial period */}
          <DetailSection title="Validity (financial periods)">
            <div className="flex flex-wrap items-center gap-1">
              {PERIODS.map((p) => {
                const on = node.periods.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePeriod(p)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      on
                        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
                        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
                    }`}
                  >
                    {periodLabel(p, fyStartMonth)}
                  </button>
                );
              })}
              <button type="button" onClick={() => onPatch({ periods: [...PERIODS] })}
                className="ml-1 text-xs text-slate-500 dark:text-slate-400 hover:underline">All</button>
              <button type="button" onClick={() => onPatch({ periods: [] })}
                className="text-xs text-slate-500 dark:text-slate-400 hover:underline">None</button>
            </div>
            <p className="m-0 mt-1.5 text-xs text-slate-400">
              {node.periods.length === 12
                ? 'Active in all 12 periods (default).'
                : node.periods.length === 0
                  ? '⚠ Not active in any period — this line will be excluded from the budget.'
                  : `Active ${validity} — excluded from the other ${12 - node.periods.length} period${12 - node.periods.length === 1 ? '' : 's'}.`}
            </p>
          </DetailSection>

          {/* Losses */}
          <DetailSection title="Losses">
            <div className="flex flex-wrap items-center gap-5">
              <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                Scrap %
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className={inputClass}
                  value={node.scrap}
                  onChange={(e) => onPatch({ scrap: Number(e.target.value) || 0 })}
                />
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                Fixed loss / batch ({node.uom})
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  className={inputClass}
                  value={node.fixedLoss}
                  onChange={(e) => onPatch({ fixedLoss: Number(e.target.value) || 0 })}
                />
              </label>
            </div>
            <p className="m-0 mt-1.5 text-xs text-slate-400">
              Scrap scales with volume; fixed loss is consumed once per batch (setup, flush, line
              start-up) — it only applies where the parent stage is batch-controlled.
            </p>
          </DetailSection>

          {/* Consumption */}
          <DetailSection title="Consumption">
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                Work centre
                <input
                  type="text"
                  className={selectClass}
                  placeholder="e.g. WC-100"
                  value={node.workCenter}
                  onChange={(e) => onPatch({ workCenter: e.target.value })}
                />
              </label>
              <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                Issue warehouse
                <select
                  className={selectClass}
                  value={node.warehouse}
                  onChange={(e) => onPatch({ warehouse: e.target.value })}
                >
                  <option value="">Not assigned</option>
                  {warehouses.map((wh) => (
                    <option key={wh.code} value={wh.code}>{wh.code} · {wh.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <p className="m-0 mt-1.5 text-xs text-slate-400">
              Work centres are free-text for now — they become a picker once the Cost / Work Centers section is wired.
            </p>
          </DetailSection>

          {/* Notes */}
          <DetailSection title="Notes">
            <textarea
              rows={3}
              className="w-full text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 resize-y"
              placeholder="e.g. supplier change planned, seasonal substitution, trial component…"
              value={node.notes}
              onChange={(e) => onPatch({ notes: e.target.value })}
            />
          </DetailSection>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-white bg-slate-900 border border-slate-900 rounded-md transition-colors hover:bg-slate-800"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- Add-component modal ------------------------------------------------------
// Two modes on one dialog:
//
//   EXISTING — pick an active stock code and add it as a direct component of the
//              current root item. Committed to the database immediately.
//   REQUEST  — the component the planner needs does not exist in md_stock_codes.
//              Rather than invent an item master record here (which is how
//              half-filled stock codes get into a system), file a request for
//              procurement: it lands in bud_requested_stock_codes with the BOM
//              context attached, and procurement completes and approves it on
//              Purchase > Requested Stock Codes. Approving there adds this very
//              line, so the planner does not have to come back and redo it.
//
// The BOM line is NOT created for a request — a line pointing at a code that may
// never be issued, or issued under a different number, is worse than no line.
export const AddComponentModal = ({
  rootCode, rootDescription, existingChildren, options, loading, busy, error,
  onAdd, onRequest, requestedNote, onClose,
}) => {
  const [mode, setMode] = useState('existing');   // 'existing' | 'request'
  const [query, setQuery] = useState('');
  const [qty, setQty] = useState('1');
  const [scrap, setScrap] = useState('0');

  // The proposed item, for a request.
  const [newCode, setNewCode] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newUom, setNewUom] = useState('');
  const [newType, setNewType] = useState('Bought-in');
  const [newNotes, setNewNotes] = useState('');

  const selected = useMemo(() => {
    const q = query.trim();
    if (!q) return null;
    return (
      options.find((o) => `${o.code} · ${o.description}` === q) ||
      options.find((o) => o.code.toLowerCase() === q.toLowerCase()) ||
      null
    );
  }, [query, options]);

  const problem = !selected
    ? (query.trim() ? 'No stock code matches — request it as a new stock code instead.' : '')
    : selected.code === rootCode
      ? "An item can't be a component of itself."
      : existingChildren.has(selected.code)
        ? 'Already a direct component of this item.'
        : '';

  const proposedCode = newCode.trim().toUpperCase();
  const requestProblem = !proposedCode
    ? ''
    : options.some((o) => o.code.toUpperCase() === proposedCode)
      ? 'That code already exists — pick it from the existing list instead.'
      : '';

  const canAdd = mode === 'existing' && selected && !problem && !busy;
  const canRequest = mode === 'request' && !!proposedCode && !!newDescription.trim() && !requestProblem && !busy;

  const tab = (on) =>
    `flex-1 px-3 py-1.5 text-xs font-semibold rounded-md border transition-colors ${
      on
        ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-200 dark:text-slate-900 dark:border-slate-200'
        : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
    }`;

  const fieldClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 '
    + 'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md max-h-full overflow-y-auto rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="sticky top-0 z-10 px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add component</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              to <span className="font-mono font-semibold">{rootCode}</span> · {rootDescription}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <div className="mb-4 flex gap-2">
            <button type="button" className={tab(mode === 'existing')} disabled={busy}
              onClick={() => setMode('existing')}>
              Existing stock code
            </button>
            <button type="button" className={tab(mode === 'request')} disabled={busy}
              onClick={() => setMode('request')}>
              Request a new one
            </button>
          </div>

          {mode === 'existing' ? (
            <>
              <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                Stock code
                <input
                  type="text"
                  list="bom-stock-codes"
                  className={fieldClass}
                  placeholder={loading ? 'Loading stock codes…' : 'Type a code or description…'}
                  value={query}
                  disabled={loading || busy}
                  onChange={(e) => setQuery(e.target.value)}
                  autoFocus
                />
                <datalist id="bom-stock-codes">
                  {options.map((o) => (
                    <option key={o.code} value={`${o.code} · ${o.description}`} />
                  ))}
                </datalist>
              </label>

              <div className="mt-4 flex items-end gap-4">
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Qty Per
                  <input
                    type="number" step="0.01" min="0"
                    className={inputClass}
                    value={qty}
                    disabled={busy}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Scrap %
                  <input
                    type="number" step="0.1" min="0"
                    className={inputClass}
                    value={scrap}
                    disabled={busy}
                    onChange={(e) => setScrap(e.target.value)}
                  />
                </label>
              </div>

              <p className="m-0 mt-2 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem || error || ''}</p>

              <p className="m-0 mt-1 text-xs text-slate-400">
                Validity, losses, work centre and notes can be set afterwards from the line detail. New lines default to all 12 periods.
              </p>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Proposed stock code <span className="text-red-600 dark:text-red-400">*</span>
                  <input
                    type="text" maxLength={100}
                    className={`${fieldClass} font-mono uppercase`}
                    placeholder="e.g. RM-NEW-001"
                    value={newCode}
                    disabled={busy}
                    onChange={(e) => setNewCode(e.target.value)}
                    autoFocus
                  />
                </label>
                <label className="col-span-2 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Description <span className="text-red-600 dark:text-red-400">*</span>
                  <input
                    type="text" maxLength={255}
                    className={fieldClass}
                    placeholder="What the item is"
                    value={newDescription}
                    disabled={busy}
                    onChange={(e) => setNewDescription(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  UoM
                  <input
                    type="text" maxLength={20}
                    className={`${fieldClass} uppercase`}
                    placeholder="KG, EA, L…"
                    value={newUom}
                    disabled={busy}
                    onChange={(e) => setNewUom(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Item type
                  <select className={fieldClass} value={newType} disabled={busy}
                    onChange={(e) => setNewType(e.target.value)}>
                    <option value="Bought-in">Bought-in</option>
                    <option value="Work in Progress">Work in Progress</option>
                    <option value="Finished Good">Finished Good</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Qty Per
                  <input
                    type="number" step="0.01" min="0"
                    className={inputClass}
                    value={qty}
                    disabled={busy}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Scrap %
                  <input
                    type="number" step="0.1" min="0"
                    className={inputClass}
                    value={scrap}
                    disabled={busy}
                    onChange={(e) => setScrap(e.target.value)}
                  />
                </label>
                <label className="col-span-2 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
                  Note for procurement
                  <textarea
                    rows={2} maxLength={500}
                    className={`${fieldClass} resize-y`}
                    placeholder="e.g. supplier already quoted, needed for the Q3 reformulation…"
                    value={newNotes}
                    disabled={busy}
                    onChange={(e) => setNewNotes(e.target.value)}
                  />
                </label>
              </div>

              <p className="m-0 mt-2 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{requestProblem || error || ''}</p>
              {requestedNote && (
                <p className="m-0 mt-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">{requestedNote}</p>
              )}

              <p className="m-0 mt-1 text-xs text-slate-400">
                This does not create a stock code or a BOM line. It sends the request — with this item, quantity and
                scrap attached — to Purchase → Requested Stock Codes. When procurement approves it, the code is created
                and this component line is added for you.
              </p>
            </>
          )}

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              {requestedNote ? 'Done' : 'Cancel'}
            </button>
            {mode === 'existing' ? (
              <button
                type="button"
                onClick={() => onAdd(selected.code, qty, scrap)}
                disabled={!canAdd}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                  canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
                }`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                {busy ? 'Adding…' : 'Add & save'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => onRequest({
                  stock_code: proposedCode,
                  description: newDescription.trim(),
                  unit_of_measure: newUom.trim().toUpperCase() || null,
                  item_type: newType,
                  notes: newNotes.trim() || null,
                  qty_per: Number(qty) || 0,
                  scrap_pct: Number(scrap) || 0,
                })}
                disabled={!canRequest}
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                  canRequest ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-400 dark:bg-slate-600'
                }`}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                {busy ? 'Sending…' : 'Send request'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---- New-BOM modal ------------------------------------------------------------
// Pick any FG/WIP item that doesn't have a BOM for this site+version yet and
// open it as a blank canvas — ready for the "Add component" flow.
export const NewBomModal = ({ candidates, onStart, onClose }) => {
  const [query, setQuery] = useState('');

  const selected = useMemo(() => {
    const q = query.trim();
    if (!q) return null;
    return (
      candidates.find((o) => `${o.code} · ${o.description}` === q) ||
      candidates.find((o) => o.code.toLowerCase() === q.toLowerCase()) ||
      null
    );
  }, [query, candidates]);

  const problem = !selected && query.trim() ? 'No item matches — pick one from the list.' : '';
  const canStart = !!selected && !problem;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">New BOM</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Start a blank bill of materials for a manufactured item at this site.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Manufactured item (FG / WIP)
            <input
              type="text"
              list="bom-new-items"
              className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400"
              placeholder="Type a code or description…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            <datalist id="bom-new-items">
              {candidates.map((o) => (
                <option key={o.code} value={`${o.code} · ${o.description}`} />
              ))}
            </datalist>
          </label>

          <p className="m-0 mt-2 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem}</p>
          <p className="m-0 mt-1 text-xs text-slate-400">
            {candidates.length === 0
              ? 'Every FG/WIP item already has a BOM at this site.'
              : 'Only items without a BOM here are listed. The BOM is saved once you add the first component.'}
          </p>

          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onStart(selected.code)}
              disabled={!canStart}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canStart ? 'bg-slate-900 hover:bg-slate-800 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              <Plus className="w-3.5 h-3.5" />
              Start BOM
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
